/* D-75 (batch 21a) · the two things that left « Vos cadeaux » because they are not assets:
   — the gift ledger (« Cadeaux gagnés ») is what happened — ruled with Clients (v3.3 WF-011): /clients?vue=cadeaux,
     crumb « Tous les clients »; /resultats?vue=cadeaux keeps showing it under the period (WF-100);
   — the règlement is the establishment's document, versioned — it lives in Paramètres › Mon établissement.
   Both keep opening the same detail screens in /cadeaux (?vue=cadeau&id=<code> · ?vue=page-publique · ?vue=generateur).
   v3.3 (pack resultats-clients): the rows are the participation ledger (mocks/data-b `participationLedger`, one source for
   every Résultats view, the client file and this list), never a fixture of four codes. Test gifts never enter it. */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Input, List, ListRow, Note, Pill, Select, Tile, type PillTone } from '@/ui'
import { apiWrites } from '@/lib/api'
import { getAsks } from '@/lib/asks'
import { currentWorld } from '@/mocks/worlds'
import { participationLedger, type LedgerRow, type LedgerStatus } from '@/mocks/data-b'

export type { LedgerRow, LedgerGift, LedgerStatus } from '@/mocks/data-b'

/* ---------- the ledger, read once per world (the asks' counts size it) ---------- */
let cache: { key: string; rows: LedgerRow[] } | null = null
/** Every participation this app knows (demo: the world's ledger; with an API: nothing until T4.1 — the views say so). */
export function getLedger(): LedgerRow[] {
  if (apiWrites) return []
  const asks = getAsks()
  const key = `${currentWorld()}|${asks.map(a => `${a.id}:${a.answers ?? 0}`).join(',')}`
  if (cache && cache.key === key) return cache.rows
  cache = { key, rows: participationLedger(currentWorld(), asks) }
  return cache.rows
}

export const STATUS: Record<LedgerStatus, { label: string; tone: PillTone }> = {
  disponible: { label: 'Disponible', tone: 'ok' },
  utilise: { label: 'Utilisé', tone: 'done' },
  expire: { label: 'Expiré', tone: 'warn' },
  annule: { label: 'Annulé', tone: 'crit' },
}
export const dateFr = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
export const dateTimeFr = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
export const giftDetailPath = (code: string) => `/cadeaux?vue=cadeau&id=${encodeURIComponent(code)}`
/** The rows of one client (the client file's « Ses cadeaux » and « Historique BMR »). */
export const rowsOfClient = (rows: LedgerRow[], clientId: string) => rows.filter(r => r.client?.id === clientId)
/** « Nom, mot-clé… » against the client, the code and the gift. */
const matches = (r: LedgerRow, q: string) => {
  const s = q.trim().toLowerCase()
  if (!s) return true
  return [r.client?.name, r.gift?.code, r.gift?.typeName].some(x => (x ?? '').toLowerCase().includes(s))
}

/** Clients › Cadeaux gagnés (WF-011) and Résultats › Cadeaux (WF-100): the list of every gift won, searchable, by state
 *  and by campaign. `rows` is the caller's — already in the report's scope on Résultats, the whole ledger under Clients. */
export function GiftLedger({ rows, campaigns, intro }: { rows: LedgerRow[]; campaigns: { id: string; name: string }[]; intro?: boolean }) {
  const nav = useNavigate()
  const [q, setQ] = useState('')
  const [etat, setEtat] = useState<'tous' | LedgerStatus>('tous')
  const [camp, setCamp] = useState('')
  const [shown, setShown] = useState(25)
  const names = Object.fromEntries(campaigns.map(c => [c.id, c.name]))
  const won = rows.filter(r => r.gift)
  const now = Date.now()
  const dispo = won.filter(r => r.gift!.status === 'disponible')
  const expiring = dispo.filter(r => Date.parse(r.gift!.expiresAt) - now < 30 * 86400e3)
  const unclaimed = rows.filter(r => !r.gift).length
  const known = dispo.filter(r => r.gift!.costCents !== null)
  const knownCents = known.reduce((a, r) => a + (r.gift!.costCents ?? 0), 0)
  const list = won.filter(r => matches(r, q) && (etat === 'tous' || r.gift!.status === etat) && (!camp || r.campaignId === camp))
  const usedCampaigns = campaigns.filter(c => won.some(r => r.campaignId === c.id))
  return (
    <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 1fr)' /* v2 · nothing here widens the page (390 px, M3) */ }} data-testid="ledger">
      {intro !== false && (<>
        <div className="tiles" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' /* v2 · the tiles wrap under 390 px */ }}>
          <Tile label="Cadeaux disponibles" value={dispo.length.toLocaleString('fr-FR')} hint={known.length ? `coût connu ${(knownCents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € sur ${known.length} · ${dispo.length - known.length} sans coût connu` : 'aucun coût connu'} />
          <Tile label="Expirent sous 30 j" value={expiring.length.toLocaleString('fr-FR')} hint="rappels envoyés automatiquement" />
          <Tile label="Non réclamés" value={unclaimed.toLocaleString('fr-FR')} hint="repartis sans e-mail · revenus au stock" />
        </div>
        <Note tone="candour"><b>Deux compteurs, jamais mélangés&nbsp;:</b> <b>les cadeaux disponibles</b> sont votre vraie dette — des cadeaux gagnés, rattachés à un client, qui seront utilisés ou expireront. <b>Non réclamés</b> = le client a quitté le parcours avant de donner son e-mail&nbsp;; après 15 minutes le cadeau revient au stock. Il ne vous a rien coûté et n’entre pas dans l’encours.</Note>
      </>)}
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', alignItems: 'end' }} data-testid="ledger-filtres">
        <label className="f-label" style={{ margin: 0 }}>Rechercher un cadeau gagné<Input aria-label="Rechercher un cadeau gagné" value={q} onChange={e => { setQ(e.target.value); setShown(25) }} placeholder="Nom, mot-clé…" style={{ marginTop: 6 }} /></label>
        <label className="f-label" style={{ margin: 0 }}>État du cadeau<Select aria-label="État du cadeau" value={etat} onChange={e => { setEtat(e.target.value as typeof etat); setShown(25) }} style={{ marginTop: 6 }}>
          <option value="tous">Tous les états</option>
          {(Object.keys(STATUS) as LedgerStatus[]).map(k => <option key={k} value={k}>{STATUS[k].label}</option>)}
        </Select></label>
        <label className="f-label" style={{ margin: 0 }}>Campagne<Select aria-label="Campagne" value={camp} onChange={e => { setCamp(e.target.value); setShown(25) }} style={{ marginTop: 6 }}>
          <option value="">Toutes les campagnes</option>
          {usedCampaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select></label>
      </div>
      <p className="f-help" style={{ margin: 0 }} data-testid="ledger-compte">{list.length.toLocaleString('fr-FR')} résultat{list.length > 1 ? 's' : ''}</p>
      <Card>
        {list.length === 0 ? (
          <p className="f-help" style={{ margin: 0 }}>{won.length === 0 ? (apiWrites ? 'Les cadeaux gagnés arrivent avec le rapport par période : pas encore mesuré.' : 'Aucun cadeau gagné pour l’instant.') : 'Aucun cadeau ne correspond à ces filtres.'}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--bmr-text-muted)' }}><th style={{ padding: '6px 8px' }}>Client / code</th><th>Cadeau</th><th>Échéance</th><th>État</th><th></th></tr></thead>
            <tbody>
              {list.slice(0, shown).map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--bmr-border)' }}>
                  <td style={{ padding: '8px' }}><div style={{ fontWeight: 600 }}>{r.client?.name ?? 'Sans prénom'}</div><div className="f-help" style={{ margin: 0, fontFamily: "'IBM Plex Mono', monospace" }}>{r.gift!.code}</div></td>
                  <td>{r.gift!.typeName}<div className="f-help" style={{ margin: 0 }}>{names[r.campaignId] ?? r.campaignId} · gagné le {dateFr(r.at)}</div></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{dateFr(r.gift!.expiresAt)}</td>
                  <td><Pill tone={STATUS[r.gift!.status].tone}>{STATUS[r.gift!.status].label}</Pill></td>
                  <td><Button size="sm" variant="quiet" onClick={() => nav(giftDetailPath(r.gift!.code))}>Ouvrir</Button></td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>
      {list.length > shown && <div><Button size="sm" onClick={() => setShown(n => n + 25)}>Afficher les 25 suivants</Button></div>}
      <p className="f-help" style={{ margin: 0 }}>Les cadeaux de test apparaissent dans le parcours de test et la caisse test. Ils sont exclus de cette liste.</p>
    </div>
  )
}

/** Mon établissement › Règlement — the versions, each frozen once published; every gift keeps a link to its version.
 *  MA-18: no page serves the public règlement until T4.5 (legal, before the beta): « page publique » waits, « bientôt ». */
const PagePublique = () => <Button size="sm" variant="quiet" disabled title="La page publique du règlement arrive avant la bêta.">page publique · bientôt</Button>
export function Reglement() {
  const nav = useNavigate()
  return (
    <div style={{ display: 'grid', gap: 10 }} data-testid="reglement">
      <List>
        <ListRow title="v. 12 — en vigueur" meta="depuis le 20 août · chances de chaque roue en service, validité 60 j, plafonds mensuels, organisateur Valessio" end={<PagePublique />} />
        <ListRow title="v. 11" meta="10 juin → 20 août · figée — des cadeaux y sont rattachés" end={<PagePublique />} />
      </List>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="sm" onClick={() => nav('/cadeaux?vue=generateur')}>Nouvelle version</Button>
        <span className="f-help">Une version publiée ne se modifie pas — on en crée une nouvelle. Chaque cadeau gagné garde un lien permanent vers sa version.</span>
      </div>
    </div>
  )
}
